import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export enum WeekDay {
  Monday = 'MON',
  Tuesday = 'TUE',
  Wednesday = 'WED',
  Thursday = 'THU',
  Friday = 'FRI',
  Saturday = 'SAT',
  Sunday = 'SUN',
}

interface MultiSelectWeekDayProps {
  size?: 'sm' | 'md';
  selectedDays: WeekDay[];
  onChange: (days: WeekDay[]) => void;
}

const MultiSelectWeekDay = ({ selectedDays, onChange, size = 'md' }: MultiSelectWeekDayProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedDays || []);

  const getWeekDayLabel = (day: WeekDay) => {
    switch (day) {
      case WeekDay.Monday:
        return t('week_days.monday');
      case WeekDay.Tuesday:
        return t('week_days.tuesday');
      case WeekDay.Wednesday:
        return t('week_days.wednesday');
      case WeekDay.Thursday:
        return t('week_days.thursday');
      case WeekDay.Friday:
        return t('week_days.friday');
      case WeekDay.Saturday:
        return t('week_days.saturday');
      case WeekDay.Sunday:
        return t('week_days.sunday');
    }
  };

  const hasNotChanged = selectedDays?.length === draft?.length && selectedDays.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedDays || []);
  }, [selectedDays]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (dataItem: WeekDay) => {
      const existItem = draft.includes(dataItem);
      const newDraft = existItem ? draft.filter((value) => value !== dataItem) : [...draft, dataItem];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('week_days.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightItem={30}
              data={Object.values(WeekDay)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: WeekDay) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item}>{getWeekDayLabel(item)}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('week_days.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                {t('week_days.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='calendar-days' />}>
        {t('week_days.title')} {(selectedDays?.length || 0) > 0 ? `(${selectedDays?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectWeekDay;
